import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ThreeDotsButton from '../../atoms/ThreeDotsButton';
import DropdownMenuFloat from '../DropdownMenuFloat';

export default function BandEventActionsMenu({
  event,
  onEdit = undefined,
  onDelete = undefined,
}) {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const eventTitle = event?.title || t('bandPage.schedule.eventActionsFallback', { defaultValue: 'event' });

  const closeMenu = useCallback(() => {
    setMenuPosition(null);
  }, []);

  const toggleMenu = useCallback((clickEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    if (menuPosition) {
      setMenuPosition(null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    setMenuPosition({
      x: rect ? rect.right : clickEvent.clientX,
      y: rect ? rect.bottom + 8 : clickEvent.clientY,
    });
  }, [menuPosition]);

  const menuItems = useMemo(() => {
    const items = [];
    if (typeof onEdit === 'function') {
      items.push({
        label: t('actions.edit', { defaultValue: 'Edit' }),
        onClick: () => onEdit(event),
      });
    }
    if (typeof onDelete === 'function') {
      items.push({
        label: t('actions.delete', { defaultValue: 'Delete' }),
        tone: 'danger',
        onClick: () => onDelete(event),
      });
    }
    return items;
  }, [event, onDelete, onEdit, t]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <div className="band-event-actions">
      <ThreeDotsButton
        ref={triggerRef}
        size={32}
        className={menuPosition ? 'is-open' : ''}
        aria-haspopup="menu"
        aria-expanded={Boolean(menuPosition)}
        aria-label={t('bandPage.schedule.eventActionsAria', {
          title: eventTitle,
          defaultValue: `Event actions for ${eventTitle}`,
        })}
        onClick={toggleMenu}
      />
      {menuPosition ? (
        <DropdownMenuFloat
          position={menuPosition}
          items={menuItems}
          onClose={closeMenu}
        />
      ) : null}
    </div>
  );
}

BandEventActionsMenu.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};
